import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Admin CRUD schemas for the inclusions / exclusions master - one physical
 * table discriminated by `type` ("inclusion" | "exclusion"). Fields: type,
 * title, and an active/inactive status.
 */

export const INCLUSION_EXCLUSION_TYPES = ['inclusion', 'exclusion'] as const;
export const InclusionExclusionTypeSchema = z.enum(INCLUSION_EXCLUSION_TYPES);
export type InclusionExclusionType = z.infer<typeof InclusionExclusionTypeSchema>;

export const InclusionExclusionListItemSchema = z.object({
  id: UuidSchema,
  type: z.string(),
  title: z.string(),
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
});
export type InclusionExclusionListItem = z.infer<typeof InclusionExclusionListItemSchema>;

export const InclusionExclusionDetailSchema = InclusionExclusionListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type InclusionExclusionDetail = z.infer<typeof InclusionExclusionDetailSchema>;

export const InclusionExclusionListQuerySchema = PaginationQuerySchema.extend({
  type: z.string().trim().min(1).optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type InclusionExclusionListQuery = z.infer<typeof InclusionExclusionListQuerySchema>;

export const InclusionExclusionListResponseSchema = paginatedResponseSchema(
  InclusionExclusionListItemSchema,
);
export type InclusionExclusionListResponse = z.infer<typeof InclusionExclusionListResponseSchema>;

const InclusionExclusionCoreFieldsSchema = z.object({
  type: InclusionExclusionTypeSchema,
  title: z.string().trim().min(1, 'Title is required').max(300),
  // Optional (not .default) to keep the Zod input/output types identical, which
  // react-hook-form's resolver typing requires. The API service defaults it to
  // true (active) on create.
  isActive: z.boolean().optional(),
});

export const CreateInclusionExclusionRequestSchema = InclusionExclusionCoreFieldsSchema;
export type CreateInclusionExclusionRequest = z.infer<typeof CreateInclusionExclusionRequestSchema>;

export const UpdateInclusionExclusionRequestSchema = InclusionExclusionCoreFieldsSchema.partial();
export type UpdateInclusionExclusionRequest = z.infer<typeof UpdateInclusionExclusionRequestSchema>;
